import { IconCheck, IconChevronDown, IconLock } from '@developerz/ui';
import { useLocation, useNavigate } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { displayLabelFor } from '../lib/display-label';
import { writeLastOrg } from '../lib/last-org';
import { findMembership, isReachable, membershipSegment, signInHref } from '../lib/memberships';
import { orgFromPath, orgSwitchHref } from '../lib/org';
import { type MembershipView, SESSION_TOKEN, useMe } from '../lib/session';
import { Popover } from './Popover';

/** Monogram for the account glyph — first letter of the login, upper-cased. */
function initial(login: string): string {
  return login.trim().slice(0, 1).toUpperCase() || '?';
}

/**
 * GitHub-style org/account switcher for the app chrome. Lists every one of the
 * signed-in user's memberships, reachable from this session or not (#466); since #460 the org lives in the URL, so choosing one
 * changes the URL (`/org/<a>/x` → `/org/<b>/x`, section kept) instead of
 * mutating the session.
 * Degrades to a static label when there's nothing to switch: a PAT / legacy
 * cookie has no memberships, and a user in a single account has only itself.
 * Renders nothing until `/v1/me` resolves — the shell shows the brand alone in
 * the meantime.
 */
export function OrgSwitcher(props: { token?: string | null }): JSX.Element {
  const token = props.token !== undefined ? props.token : SESSION_TOKEN;
  const me = useMe(token);
  const navigate = useNavigate();
  const location = useLocation();

  const memberships = (): readonly MembershipView[] => me.data?.memberships ?? [];
  // The org named by the current URL (reactive via the router location).
  const locationOrg = (): string | null => orgFromPath(location.pathname);
  // The acting account: the URL org when on an `/org/:org` route (matched by
  // login, case-insensitively like GitHub, or by id), else the session's
  // server-side active account (legacy paths, PATs).
  const activeId = (): string | undefined => {
    const org = locationOrg();
    if (org !== null) {
      return findMembership(memberships(), org)?.account_id ?? org;
    }
    return me.data?.active_account_id;
  };
  // Prefer the active membership's login; fall back to the URL org, then the
  // resolved account (a PAT / machine credential has no memberships but still
  // names its account). The display label is the account's `developerz-ai-
  // internal` rename (#1153) for the production org, else the login.
  const activeLabel = (): string =>
    displayLabelFor(
      memberships().find((m) => m.account_id === activeId())?.github_login ??
        locationOrg() ??
        me.data?.github_login ??
        '',
    );
  const canSwitch = (): boolean => memberships().length > 1;

  /**
   * Switch tenant with a FULL DOCUMENT LOAD, not a client-side navigation
   * (#921).
   *
   * A push re-keys the account-scoped queries and TanStack refetches them, which
   * is correct for everything that goes through a query. It is not everything:
   * module-level signals, open SSE subscriptions, drawer/dialog state, anything
   * a view derived once at mount, and any query whose key someone forgot to
   * scope all survive the push and keep describing the previous org. Each of
   * those is its own bug and they keep being found one at a time.
   *
   * Reloading drops the whole JS heap and rebuilds it under the new tenant, so
   * "did this particular piece of state get re-derived?" stops being a question
   * anyone has to answer per view. The cost is one document load per switch —
   * an action taken a handful of times a day, already followed by a burst of
   * refetches — in exchange for the class of bug going away.
   *
   * `location.assign` (not `replace`) so Back still returns to the previous org.
   *
   * #466 § 4: a membership this session cannot open (its org signs in through
   * its own IdP) goes through that org's sign-in instead, `return_to` the same
   * section in it; the sign-in adds the org to this session. Only a reachable
   * choice is remembered (`dz_last_org`): the detour may not come back, and
   * `<OrgScope/>` records the org once it does open.
   */
  function choose(m: MembershipView, close: () => void): void {
    close();
    if (m.account_id === activeId()) return;
    // The login is the segment when the account has one; the id also resolves
    // server-side (X-Dz-Account accepts either).
    const target = orgSwitchHref(location.pathname, membershipSegment(m));
    // Guard for non-browser callers (tests render this without a real location).
    if (typeof window === 'undefined' || !window.location) {
      navigate(target);
      return;
    }
    const detour = isReachable(m) ? null : signInHref(m, target, window.location.origin);
    if (isReachable(m)) writeLastOrg(membershipSegment(m));
    window.location.assign(detour ?? target);
  }

  const path = (): JSX.Element => (
    <span class="org-switcher__path">
      <span class="org-switcher__prefix">{t('org_switcher.prefix')}</span>
      <span class="org-switcher__name">{activeLabel()}</span>
    </span>
  );

  // Menu mechanics are <Popover/>'s: outside click, Escape (only when no dialog
  // sits above it), and focus back on the trigger however the menu closes.
  return (
    <Show when={me.data && activeLabel()}>
      <Show
        when={canSwitch()}
        fallback={
          <div class="org-switcher org-switcher--static">
            <div class="org-switcher__current">{path()}</div>
          </div>
        }
      >
        <Popover
          class="org-switcher"
          // The name carries the `org / <name>` the trigger shows (WCAG 2.5.3, label
          // in name, axe-core's `label-content-name-mismatch` from 4.14).
          label={t('org_switcher.label', {
            path: `${t('org_switcher.prefix')} ${activeLabel()}`,
          })}
          triggerClass="org-switcher__current"
          panelClass="org-switcher__menu"
          trigger={
            <>
              {path()}
              <IconChevronDown class="org-switcher__chevron" size="0.875rem" />
            </>
          }
        >
          {(close) => (
            <ul>
              <For each={memberships()}>
                {(m) => (
                  <li>
                    <button
                      type="button"
                      class="org-switcher__item"
                      aria-current={m.account_id === activeId() ? 'true' : undefined}
                      onClick={() => choose(m, close)}
                    >
                      <span class="org-switcher__avatar" aria-hidden="true">
                        {initial(m.github_login ?? '')}
                      </span>
                      <span class="org-switcher__text">
                        <span class="org-switcher__name">
                          {displayLabelFor(m.github_login ?? '')}
                        </span>
                        {/* The sign-in tag shares the role's line, never the
                            name's: beside the name it squeezed the login to an
                            ellipsis in the rail's width. */}
                        <span class="org-switcher__meta">
                          <span class="org-switcher__role">{m.role}</span>
                          <Show when={!isReachable(m)}>
                            <span class="org-switcher__sign-in">
                              <IconLock size="0.75rem" />
                              {t('org_switcher.sign_in')}
                            </span>
                          </Show>
                        </span>
                      </span>
                      <Show when={m.account_id === activeId()}>
                        <IconCheck class="org-switcher__check" size="1rem" />
                      </Show>
                    </button>
                  </li>
                )}
              </For>
            </ul>
          )}
        </Popover>
      </Show>
    </Show>
  );
}
